import { DndContext, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { Card, Empty, Flex, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import type {
  ProjectFormData,
  ProjectFormDataPhase
} from "../../../project-form-data.ts"
import { MilestonesFooterRow } from "./MilestonesFooterRow.tsx"
import { MilestonesHeaderRow } from "./MilestonesHeaderRow.tsx"
import { type MilestoneField, MilestonesItem } from "./MilestonesItem.tsx"

interface ProjectTypeMilestonesProps {
  form: FormInstance<ProjectFormData>
  activeProjectTypeId?: string | null
}

export function Milestones({
  form,
  activeProjectTypeId
}: ProjectTypeMilestonesProps) {
  const name: keyof ProjectFormData = "project_phases"

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const currentPhases = form.getFieldValue(name) || []
    const activeIndex = currentPhases.findIndex(
      (p: ProjectFormDataPhase) => p.id === active.id
    )
    const overIndex = currentPhases.findIndex(
      (p: ProjectFormDataPhase) => p.id === over.id
    )

    if (activeIndex !== -1 && overIndex !== -1) {
      const newPhases = arrayMove(currentPhases, activeIndex, overIndex).map(
        (phase: any, index: number) => ({
          ...phase,
          order: index
        })
      )

      // Update form values
      form.setFieldValue("project_phases", newPhases)
    }
  }

  return (
    <div className={"grid overflow-auto"}>
      <MilestonesHeaderRow />
      <Form.List
        name={name}
        rules={[
          {
            validator: async (_, milestones: ProjectFormDataPhase[]) => {
              const firstErrorField = milestones?.find((m) => !m.name?.trim())

              if (firstErrorField) {
                form.setFieldValue(
                  "activeProjectTypeId",
                  firstErrorField?.project_type
                )
                return Promise.reject(new Error("Please check milestones"))
              }

              return Promise.resolve()
            }
          }
        ]}
      >
        {(fields, { remove }, { errors }) => {
          const fullFields = fields?.reduce<{
            ids: string[]
            fields: MilestoneField[]
          }>(
            (acc, f) => {
              const phase: ProjectFormDataPhase = form.getFieldValue([
                name,
                f?.name
              ])

              const showShow =
                phase?.project_type &&
                phase?.project_type === activeProjectTypeId

              if (!showShow) return acc

              acc.fields.push({ ...f, phase })
              acc.ids.push(phase?.id)

              return acc
            },
            { ids: [], fields: [] }
          )

          if (!fullFields?.fields?.length) {
            return (
              <Card size={"small"}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={"No milestones found"}
                />
              </Card>
            )
          }

          return (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fullFields?.ids}
                strategy={verticalListSortingStrategy}
              >
                <Flex vertical>
                  {fullFields?.fields?.map(({ phase, ...field }) => (
                    <React.Fragment key={phase?.id}>
                      <MilestonesItem
                        id={phase.id}
                        fieldName={field?.name}
                        form={form}
                        onRemove={() => remove(field?.name)}
                      />
                    </React.Fragment>
                  ))}
                  <MilestonesFooterRow
                    activeProjectTypeId={activeProjectTypeId}
                    form={form}
                  />
                </Flex>
              </SortableContext>
              <Form.Item noStyle>
                <Form.ErrorList className={"text-error"} errors={errors} />
              </Form.Item>
            </DndContext>
          )
        }}
      </Form.List>
    </div>
  )
}
